/**
 * BETA feature set is used for features that are still in development.
 * The value of BETA is set to the maximum safe integer to ensure that it is always greater than any
 * other feature set value.
 */
export const BETA = Number.MAX_SAFE_INTEGER;

/**
 * Pharia AI feature set.
 *
 *  This determines the feature set the environment is in. The higher the value, the more features are enabled.
 *  If no feature set is set, the default is BETA. BETA is the highest value and enables all features.
 */
export class PhariaAiFeatureSet {
  value: number;

  constructor(value: number) {
    this.value = value;
  }

  toString(): string {
    return this.value === BETA ? 'BETA' : this.value.toString();
  }

  /**
   * Used to initialize the global feature set from the environment variable.
   */
  static fromEnv(): PhariaAiFeatureSet {
    const featureSet = Number(import.meta.env.VITE_PHARIA_AI_FEATURE_SET);
    // If the feature set is not a number, default to BETA.
    return isNaN(featureSet) ? new PhariaAiFeatureSet(BETA) : new PhariaAiFeatureSet(featureSet);
  }
}

/**
 * Const object for all features.
 */
export const Feature = {
  PROJECT_PERMISSIONS: 'PROJECT_PERMISSIONS',
  FINETUNING: 'FINETUNING',
  CHAT: 'CHAT',
  APPLICATION_ACCESS_MANAGEMENT: 'APPLICATION_ACCESS_MANAGEMENT',
  KERNEL_INTEGRATION: 'KERNEL_INTEGRATION',
  APPLICATION_TRACING: 'APPLICATION_TRACING',
  EVALUATION_IMPROVEMENTS: 'EVALUATION_IMPROVEMENTS',
  EXPLAINABILITY: 'EXPLAINABILITY',
  GROUPS_APPLICATION: 'GROUPS_APPLICATION',
  RAG: 'RAG',
} as const;

export type Feature = (typeof Feature)[keyof typeof Feature];

/**
 * Feature states for all features. This determines what feature set the feature should be part of.
 *
 * To add a new feature, add it to the Feature enum and add it to this dict.
 */
export const FEATURE_STATES: Record<Feature, PhariaAiFeatureSet> = {
  [Feature.PROJECT_PERMISSIONS]: new PhariaAiFeatureSet(250600),
  [Feature.FINETUNING]: new PhariaAiFeatureSet(1),
  [Feature.CHAT]: new PhariaAiFeatureSet(1),
  [Feature.APPLICATION_ACCESS_MANAGEMENT]: new PhariaAiFeatureSet(250800),
  [Feature.KERNEL_INTEGRATION]: new PhariaAiFeatureSet(250700),
  [Feature.APPLICATION_TRACING]: new PhariaAiFeatureSet(251000),
  [Feature.EVALUATION_IMPROVEMENTS]: new PhariaAiFeatureSet(251100),
  [Feature.EXPLAINABILITY]: new PhariaAiFeatureSet(BETA),
  [Feature.RAG]: new PhariaAiFeatureSet(260100),
  [Feature.GROUPS_APPLICATION]: new PhariaAiFeatureSet(251200),
};

/**
 * Manages feature flags and their states. Inspired by https://github.com/Aleph-Alpha/finetuning/pull/171/files.
 *  This class is used to check if a feature is enabled in the current environment.
 *
 * It can be used as follows:
 * ```typescript
 * import { featureFlagManager } from './featureflag_manager';
 *
 * if (featureFlagManager.isActive(Feature.PROJECT_PERMISSIONS)) {
 *  // Feature is active
 * } else {
 * // Feature is not active
 * }
 * ```
 */
export class FeatureFlagManager {
  public globalFeatureSet: PhariaAiFeatureSet;
  private _features: Record<Feature, PhariaAiFeatureSet>;

  /**
   * Initializes the feature flag manager by setting up the feature states and the global feature set.
   * @param featureEnum - The feature enum to check against.
   * @param featureStates - The states of the features.
   */
  constructor(featureEnum = Feature, featureStates = FEATURE_STATES) {
    this._features = Object.values(featureEnum).reduce(
      (acc, feature) => {
        acc[feature] = featureStates[feature];
        return acc;
      },
      {} as Record<Feature, PhariaAiFeatureSet>
    );

    this.globalFeatureSet = PhariaAiFeatureSet.fromEnv();
  }

  /**
   * Checks if a given feature is enabled.
   *
   * @param feature - The feature to check.
   * @returns `true` if the feature is active, `false` otherwise.
   * @throws Will throw an error if the feature is unknown.
   */
  isActive(feature: Feature): boolean {
    if (!(feature in this._features)) {
      throw new Error(`Unknown feature: ${feature}`);
    }
    return this._features[feature].value <= this.globalFeatureSet.value;
  }
}

export const featureFlagManager = new FeatureFlagManager();
